import { bff, createBFFHandlers } from '~/framework/bff';
import { z } from 'zod';
import { getServerSideMonolithClient } from '~/framework';
import {
   getCourseAssignmentOperatorDetails as getCourseAssignmentOperatorDetailsFromMonolith,
   AssessmentCompletion as MonolithAssessmentCompletion,
   StageCompletionRecord as MonolithStageCompletion,
} from '~/monolith-api';
import { CommonRequestOptions } from '~/monolith-api/makeRequest';

export type { CourseAssignmentOperatorDetails, StageCompletionRecord } from '~/monolith-api';

const getCourseAssignmentOperatorDetailsSchema = z.object({
   courseAssignmentId: z.number().nullable(),
});

export async function getCourseAssignmentOperatorDetails(
   courseAssignmentId: number | null,
   options: CommonRequestOptions
) {
   const result = await getCourseAssignmentOperatorDetailsFromMonolith(courseAssignmentId, options);

   return {
      ...result,
      courseAssignment: {
         ...(result.courseAssignment ?? []),
         user_completion_status: await Promise.all(
            result.courseAssignment?.user_completion_status?.map(getStageDetails)
         ),
      },
   };
}

async function getStageDetails(stageCompletion: MonolithStageCompletion) {
   const assessmentCompletions = stageCompletion.assessments;

   if (!assessmentCompletions) {
      return {
         ...stageCompletion,
         assessments: [],
      };
   }

   return {
      ...stageCompletion,
      assessments: await Promise.all(
         assessmentCompletions.map(assessment => {
            return getAssessmentDetails(assessment);
         })
      ),
   };
}

async function getAssessmentDetails(assessment: MonolithAssessmentCompletion) {
   switch (assessment.assessmenttype) {
      case 'trainer-sign-off':
         return {
            ...assessment,
            is_completed: assessment.is_completed ?? false,
            request: assessment.signoff_request ?? null,
         };
      case 'quiz':
         return {
            ...assessment,
         };
   }
}

export default createBFFHandlers({
   post: async (req, res) => {
      const { courseAssignmentId } = getCourseAssignmentOperatorDetailsSchema.parse(req.body);
      const monolithClient = getServerSideMonolithClient(req);
      const result = await getCourseAssignmentOperatorDetails(courseAssignmentId, {
         apiClient: monolithClient,
      });
      res.status(200).json(result);
   },
});

export const getCourseAssignmentOperatorDetailsFromClient = (
   courseAssignmentId: number | null
): ReturnType<typeof getCourseAssignmentOperatorDetails> => {
   return bff()
      .post(`/courses/getCourseAssignmentOperatorDetails`, {
         courseAssignmentId,
      })
      .then(response => response.data);
};

export type GetCourseAssignmentOperatorDetailsErrorCode =
   | 'courses.course_assignment_details_not_found'
   | 'course_assignments.get_operator_course_assignment_details.not_assignee';

export type GetCourseAssignmentOperatorDetailsResult = Awaited<
   ReturnType<typeof getCourseAssignmentOperatorDetails>
>;

export type CourseProgress = GetCourseAssignmentOperatorDetailsResult['courseAssignment'];

export type StageProgress = CourseProgress['user_completion_status'][number];

export type StageStatus = StageProgress['status'];

export type Stage = CourseProgress['course']['stages'][number];

export type AssessmentProgress = StageProgress['assessments'][number];

export type SignOffProgress = AssessmentProgress & { assessmenttype: 'trainer-sign-off' };
